import { isKeyHotkey } from 'is-hotkey';

import type { Change, Value } from '@gitbook/slate';
import type { EditorContainer, Plugin } from '@gitbook/slate-react';
import { assertIsDefined } from '@gitbook/util-ts';

type MatcherFn = (type: string) => boolean;
type TriggerFn = (event: InputEvent | CompositionEvent) => boolean;

type MatcherInput = MatcherFn | string[] | string;
type TriggerInput = TriggerFn | RegExp | string;

interface Matches<BeforeRegExp extends RegExp | undefined, AfterRegExp extends RegExp | undefined> {
    after: AfterRegExp extends RegExp ? string[] : null;
    before: BeforeRegExp extends RegExp ? string[] : null;
}

/*
 * A Slate plugin to automatically replace a block when a string of matching
 * text is typed.
 */
function AutoReplace<
    BeforeRegExp extends RegExp | undefined,
    AfterRegExp extends RegExp | undefined,
>(opts: {
    // Trigger on the given input text
    onInput?: TriggerInput;
    // Trigger on the given hotkey, given in the format of `is-hotkey`
    onHotkey?: string;
    // Only trigger inside the given node types
    onlyIn?: MatcherInput;
    // Never trigger in the given node types
    ignoreIn?: MatcherInput;
    // Trigger if the text before the cursor matches the regexp
    // Any captured group in the regexp will be removed on transform.
    before?: BeforeRegExp;
    // Trigger if the text after the cursor matches the regexp
    // Any captured group in the regexp will be removed on transform.
    after?: AfterRegExp;

    // What to change when triggered
    transform: (
        change: Change,
        event: Event,
        matches: Matches<BeforeRegExp, AfterRegExp>,
        editor: EditorContainer
    ) => void;

    // Allow users to revert the auto-replace on backspace
    revertOnBackspace?: {
        from: RegExp;
        to: string | ((match: string) => string);
    };
}): Plugin {
    const { transform } = opts;
    const onHotkey = opts.onHotkey ? isKeyHotkey(opts.onHotkey) : () => false;
    const onInput = opts.onInput ? normalizeOnInput(opts.onInput) : () => false;
    const ignoreIn = opts.ignoreIn ? normalizeMatcher(opts.ignoreIn) : null;
    const onlyIn = opts.onlyIn ? normalizeMatcher(opts.onlyIn) : null;

    function onKeyDown(
        event: KeyboardEvent,
        change: Change,
        editor: EditorContainer
    ): boolean | void {
        if (!onHotkey(event)) {
            return;
        }

        const matches = getMatches(change.value);
        if (!matches) {
            return;
        }

        replace(matches, event, change, editor);
        return true;
    }

    function onBeforeInput(
        event: InputEvent,
        change: Change,
        editor: EditorContainer
    ): boolean | void {
        if (event.inputType !== 'insertText') {
            return;
        }

        if (!onInput(event)) {
            return;
        }

        const matches = getMatches(change.value);
        if (!matches) {
            return;
        }

        replace(matches, event, change, editor);
        return true;
    }

    function onCompositionEnd(
        event: CompositionEvent,
        change: Change,
        editor: EditorContainer
    ): boolean | void {
        if (!onInput(event)) {
            return;
        }

        // Try to find matches without the inserted character
        const matches = getMatches(change.value.change().deleteBackward(event.data.length).value);
        if (!matches) {
            return;
        }

        replace(matches, event, change.deleteBackward(event.data.length), editor);
        return true;
    }

    function onRevertKeyDown(event: KeyboardEvent, change: Change): boolean | void {
        const revert = opts.revertOnBackspace;
        if (!revert || !isKeyHotkey('backspace')(event)) {
            return;
        }

        const { from, to } = revert;
        const { startText, startOffset } = change.value;
        if (!startText) return;

        const textBeforeCursor = startText.text.slice(0, startOffset);
        const match = textBeforeCursor.match(from);
        if (!match) {
            return;
        }

        const replacement = typeof to === 'function' ? to(match[0].trim()) : to;

        change.deleteBackward(match[0].length).insertText(`${replacement}`);

        event.preventDefault();
        return true;
    }

    /*
     * Apply the transform it.
     */
    function replace(
        matches: Matches<BeforeRegExp, AfterRegExp>,
        event: Event,
        change: Change,
        editor: EditorContainer
    ): boolean | void {
        const { value } = change;
        let { startOffset } = value;
        let totalRemoved = 0;
        const offsets = getOffsets(matches, startOffset);

        offsets.forEach((offset) => {
            change.moveOffsetsTo(offset.start, offset.end).delete();

            totalRemoved += offset.total;
        });

        startOffset -= totalRemoved;
        change.moveOffsetsTo(startOffset, startOffset);

        change.call(transform, event, matches, editor);

        event.preventDefault();
        return true;
    }

    /*
     * Try to match the current text of a `value` with the `before` and
     * `after` regexes, and other given conditions.
     */
    function getMatches(value: Value): Matches<BeforeRegExp, AfterRegExp> | null {
        if (value.isExpanded) {
            return null;
        }

        const { startBlock } = value;
        if (!startBlock) {
            return null;
        }

        const { type } = startBlock;
        if (onlyIn && !onlyIn(type)) {
            return null;
        }

        if (ignoreIn) {
            if (ignoreIn(type) || value.marks.some((mark) => ignoreIn(mark.type))) {
                return null;
            }
        }

        const { startText, startOffset } = value;

        assertIsDefined(startText);

        const { text } = startText;
        let after: string[] | null = null;
        let before: string[] | null = null;

        if (opts.after) {
            const content = text.slice(startOffset);
            after = content.match(opts.after);
        }

        if (opts.before) {
            const content = text.slice(0, startOffset);
            before = content.match(opts.before);
        }

        // If both sides, require that both are matched, otherwise null.
        if (opts.before && opts.after && !before) {
            after = null;
        }
        if (opts.before && opts.after && !after) {
            before = null;
        }

        // Return null unless we have a match.
        if (!before && !after) {
            return null;
        }

        if (after) {
            after[0] = after[0].replace(/\s+$/, '');
        }
        if (before) {
            before[0] = before[0].replace(/^\s+/, '');
        }

        // @ts-ignore
        return { before, after };
    }

    /*
     * Return the offsets for `matches` with `start` offset.
     */
    function getOffsets(
        matches: { after: string[] | null; before: string[] | null },
        start: number
    ) {
        const { before, after } = matches;
        const offsets: Array<{
            start: number;
            end: number;
            total: number;
        }> = [];
        let totalRemoved = 0;

        if (before) {
            const match = before[0];
            let startOffset = 0;
            let matchIndex = 0;

            before.slice(1, before.length).forEach((current) => {
                if (current === undefined) {
                    return;
                }

                matchIndex = match.indexOf(current, matchIndex);
                startOffset = start - totalRemoved + matchIndex - match.length;

                offsets.push({
                    start: startOffset,
                    end: startOffset + current.length,
                    total: current.length,
                });

                totalRemoved += current.length;
                matchIndex += current.length;
            });
        }

        if (after) {
            const match = after[0];
            let startOffset = 0;
            let matchIndex = 0;

            after.slice(1, after.length).forEach((current) => {
                if (current === undefined) {
                    return;
                }

                matchIndex = match.indexOf(current, matchIndex);
                startOffset = start - totalRemoved + matchIndex;

                offsets.push({
                    start: startOffset,
                    end: startOffset + current.length,
                    total: 0,
                });

                totalRemoved += current.length;
                matchIndex += current.length;
            });
        }

        return offsets;
    }

    return {
        onKeyDown: (event, change, editor) =>
            onRevertKeyDown(event, change) || onKeyDown(event, change, editor),
        onBeforeInput,
        onCompositionEnd,
    };
}

/*
 * Normalize a 'onInput' option to a matching function.
 */
function normalizeOnInput(onInput: TriggerInput): TriggerFn {
    if (typeof onInput === 'string') {
        return (event) => !!(event.data && event.data === onInput);
    }

    if (typeof onInput === 'function') {
        return onInput;
    }

    if (onInput instanceof RegExp) {
        return (event) => !!event.data?.match(onInput);
    }
    throw new Error('Invalid onInput option');
}

/*
 * Normalize a node matching plugin option.
 */
function normalizeMatcher(matcher: MatcherInput): MatcherFn {
    if (Array.isArray(matcher)) {
        return (type: string) => matcher.includes(type);
    }

    if (typeof matcher === 'string') {
        return (type: string) => type === matcher;
    }

    if (typeof matcher === 'function') {
        return matcher;
    }

    throw new Error('Invalid matcher type');
}

export default AutoReplace;
